import { Paperclip, Send, User, X } from "lucide-react";
import { useDataProvider, useNotify, type Identifier } from "ra-core";
import { useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { fullName, type GulaClient } from "../gulaTypes";
import { agentName, type Agent } from "../useAgents";
import type { TeamAttachment } from "./teamChat";

const BUCKET = "attachments";

const uploadFile = async (file: File): Promise<TeamAttachment> => {
  const safeName = file.name.replace(/[^\w.-]+/g, "_");
  const path = `team/${crypto.randomUUID()}-${safeName}`;
  const storage = getSupabaseClient().storage.from(BUCKET);
  const { error } = await storage.upload(path, file, {
    contentType: file.type || undefined,
  });
  if (error) throw new Error(`No se ha podido subir ${file.name}`);
  return {
    src: storage.getPublicUrl(path).data.publicUrl,
    title: file.name,
    type: file.type || undefined,
  };
};

const normalize = (text: string) =>
  text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/** Writes a message, or a task for a teammate when "Para" is set. */
export const TeamComposer = ({
  agents,
  meId,
  clients,
  onSent,
}: {
  agents: Agent[];
  meId?: Identifier;
  clients: GulaClient[];
  onSent: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const fileRef = useRef<HTMLInputElement>(null);
  const [body, setBody] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [client, setClient] = useState<GulaClient | null>(null);
  const [clientQuery, setClientQuery] = useState("");
  const [pickingClient, setPickingClient] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  const matches = useMemo(() => {
    const query = normalize(clientQuery.trim());
    if (query.length < 2) return [];
    const digits = clientQuery.replace(/\D/g, "");
    return clients
      .filter(
        (item) =>
          normalize(fullName(item)).includes(query) ||
          (digits.length >= 3 &&
            (item.phone ?? "").replace(/\D/g, "").includes(digits)),
      )
      .slice(0, 6);
  }, [clientQuery, clients]);

  const reset = () => {
    setBody("");
    setAssigneeId("");
    setDueDate("");
    setClient(null);
    setClientQuery("");
    setPickingClient(false);
    setFiles([]);
  };

  const send = async () => {
    const text = body.trim();
    if (!text && !files.length) return;
    setSending(true);
    try {
      const attachments = await Promise.all(files.map(uploadFile));
      await dataProvider.create("gula_team_messages", {
        data: {
          sales_id: meId,
          kind: assigneeId ? "task" : "message",
          body: text || files.map((file) => file.name).join(", "),
          assignee_id: assigneeId ? Number(assigneeId) : null,
          due_date: assigneeId && dueDate ? dueDate : null,
          contact_id: client?.contact_id ?? null,
          attachments,
        },
      });
      reset();
      onSent();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido enviar",
        { type: "error" },
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 border-t p-3">
      <Textarea
        value={body}
        rows={2}
        placeholder={
          assigneeId
            ? "¿Qué tiene que hacer? (Enter para enviar)"
            : "Escribe al equipo… (Enter envía, Mayús+Enter salta de línea)"
        }
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            send();
          }
        }}
        className="min-h-[60px] resize-none"
      />

      {files.length ? (
        <div className="flex flex-wrap gap-1.5">
          {files.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-0.5 text-xs"
            >
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Quitar ${file.name}`}
                onClick={() =>
                  setFiles((previous) =>
                    previous.filter((_, position) => position !== index),
                  )
                }
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {pickingClient && !client ? (
        <div className="relative">
          <Input
            autoFocus
            value={clientQuery}
            placeholder="Buscar cliente por nombre o teléfono"
            onChange={(event) => setClientQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setPickingClient(false);
            }}
          />
          {matches.length ? (
            <ul className="absolute bottom-full z-10 mb-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
              {matches.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted"
                    onClick={() => {
                      setClient(item);
                      setPickingClient(false);
                    }}
                  >
                    <span className="truncate">{fullName(item)}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {item.phone ?? item.city ?? ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          Para
          <select
            aria-label="Asignar a"
            value={assigneeId}
            onChange={(event) => setAssigneeId(event.target.value)}
            className="rounded-md border bg-background px-1.5 py-1 text-xs text-foreground"
          >
            <option value="">Todos (mensaje)</option>
            {agents.map((agent) => (
              <option key={agent.id} value={String(agent.id)}>
                {String(agent.id) === String(meId)
                  ? `${agent.first_name} (yo)`
                  : agentName(agent)}
              </option>
            ))}
          </select>
        </label>
        {assigneeId ? (
          <Input
            type="date"
            aria-label="Para cuándo"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
            className="h-7 w-auto px-1.5 text-xs"
          />
        ) : null}
        {client ? (
          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs">
            <User className="h-3 w-3" />
            {fullName(client)}
            <button
              type="button"
              aria-label="Quitar cliente"
              onClick={() => setClient(null)}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setPickingClient((open) => !open)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <User className="h-3.5 w-3.5" />
            Cliente
          </button>
        )}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <Paperclip className="h-3.5 w-3.5" />
          Adjuntar
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            const picked = Array.from(event.target.files ?? []);
            setFiles((previous) => [...previous, ...picked]);
            event.target.value = "";
          }}
        />
        <Button
          size="sm"
          className="ml-auto"
          onClick={send}
          disabled={sending || (!body.trim() && !files.length)}
        >
          <Send className="h-4 w-4" />
          {sending ? "Enviando…" : assigneeId ? "Asignar" : "Enviar"}
        </Button>
      </div>
    </div>
  );
};
